/**
 * ProUpgradeModal — "Get Pro" upsell for Live Heart Monitor + HRV.
 *
 * Pricing per user choice (c + f): $59 one-time add-on on top of the
 * existing $9.99 Premium. The 22-day trial is NO-CARD (the user chose f),
 * so the primary CTA is "Start 22-day free trial" and the secondary CTA
 * is "Buy now for $59 · lifetime". No scary pricing wall, but we still
 * surface it so expectations are set.
 *
 * States rendered:
 *   idle       — default, both CTAs visible.
 *   startingTrial  — trial in flight.
 *   checkingOut    — Stripe roundtrip in flight (button spinner).
 *
 * Auth gate: if the user isn't signed in, we redirect them to login rather
 * than failing silently — Pro is attached to an account, not a device.
 */
import React, { useEffect, useState } from 'react';
import { X, HeartPulse, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';

export const ProUpgradeModal = ({ open, onClose }) => {
  const { user } = useAuth();
  const {
    isPro, proStatus, proTrialDaysLeft,
    startProTrial, beginProCheckout,
  } = useSubscription();

  const [busy, setBusy] = useState(null); // 'trial' | 'checkout' | null
  const [pricing, setPricing] = useState(null);

  // Lazy-fetch pricing once on open so the card shows real values from
  // the backend rather than hard-coded numbers that could drift.
  useEffect(() => {
    if (!open || pricing) return;
    const ctrl = new AbortController();
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pro/pricing`, {
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then(setPricing)
      .catch(() => {});
    return () => ctrl.abort();
  }, [open, pricing]);

  if (!open) return null;

  const handleStartTrial = async () => {
    if (!user) {
      toast.error('Please sign in to start your Pro trial');
      onClose?.();
      return;
    }
    setBusy('trial');
    const res = await startProTrial();
    setBusy(null);
    if (res.success) {
      toast.success(res.message || 'Pro trial started!');
      onClose?.();
    } else {
      toast.error(res.message || 'Could not start trial');
    }
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please sign in to purchase Pro');
      onClose?.();
      return;
    }
    setBusy('checkout');
    const res = await beginProCheckout();
    if (res.success && res.url) {
      window.location.href = res.url;
    } else {
      setBusy(null);
      toast.error(res.message || 'Could not open checkout');
    }
  };

  const price = pricing?.price ?? 59;
  const trialDays = pricing?.trialDays ?? 22;
  const includes = pricing?.includes ?? [
    'Live heart-rate monitor pairing (Web Bluetooth)',
    'Real-time HRV (RMSSD) + Rhythm Score',
    '30-day HRV dashboard + session snapshots',
    'Heartbeat audio synced to your real R-peaks',
    'Lifetime access — never expires',
  ];

  const alreadyActive = isPro && proStatus === 'active';
  const trialActive = isPro && proStatus === 'trial';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      data-testid="pro-upgrade-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg rounded-2xl border border-rose-400/25 bg-gradient-to-br from-[#1a0320] via-[#0f0218] to-[#0a0118] p-6 sm:p-8 shadow-2xl shadow-rose-500/10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="pro-upgrade-modal-close"
          className="absolute right-4 top-4 text-white/40 hover:text-white/80 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center">
            <HeartPulse className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-300/90">
              NeuroWave Pro · biofeedback add-on
            </div>
            <h2 className="text-white text-xl sm:text-2xl font-semibold">
              Hear your own heart in real time
            </h2>
          </div>
        </div>

        <p className="text-white/55 text-sm leading-relaxed mt-3">
          Pair a Polar H10 or any Bluetooth chest strap. The heartbeat audio
          locks to your actual R-peaks, and a live dashboard tracks your
          RMSSD and Rhythm Score across 30 days — the same physiological
          markers HeartMath, Polar and Whoop track.
        </p>

        {/* Price card */}
        <div
          className="mt-5 rounded-xl border border-white/8 bg-white/[0.03] p-5"
          data-testid="pro-price-card"
        >
          <div className="flex items-baseline gap-2">
            <span className="text-white text-4xl font-semibold tabular-nums">
              ${price}
            </span>
            <span className="text-white/40 text-sm">/ lifetime · one-time</span>
          </div>
          <div className="text-xs text-emerald-300 mt-1">
            Free for {trialDays} days — no card required
          </div>
          <ul className="mt-4 space-y-1.5 text-sm">
            {includes.map((line) => (
              <li key={line} className="flex items-start gap-2 text-white/70">
                <Check className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTAs */}
        <div className="mt-6 flex flex-col gap-2">
          {alreadyActive ? (
            <div
              className="rounded-lg bg-emerald-500/10 border border-emerald-400/20 px-4 py-3 text-emerald-200 text-sm text-center"
              data-testid="pro-already-active"
            >
              You already have NeuroWave Pro — enjoy.
            </div>
          ) : trialActive ? (
            <>
              <div
                className="rounded-lg bg-emerald-500/10 border border-emerald-400/20 px-4 py-2.5 text-emerald-200 text-sm text-center"
                data-testid="pro-trial-active-banner"
              >
                Pro trial active · {proTrialDaysLeft ?? 0} days left
              </div>
              <Button
                type="button"
                onClick={handleCheckout}
                disabled={busy === 'checkout'}
                data-testid="pro-checkout-now"
                className="w-full bg-rose-500 hover:bg-rose-400 text-white"
              >
                {busy === 'checkout' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
                ) : (
                  <>Keep Pro for ${price} · lifetime</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                onClick={handleStartTrial}
                disabled={busy !== null}
                data-testid="pro-start-trial-button"
                className="w-full bg-rose-500 hover:bg-rose-400 text-white h-11"
              >
                {busy === 'trial' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting trial…</>
                ) : (
                  <>Start {trialDays}-day free trial</>
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleCheckout}
                disabled={busy !== null}
                data-testid="pro-checkout-button"
                className="w-full text-white/70 hover:text-white hover:bg-white/5 h-10"
              >
                {busy === 'checkout' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
                ) : (
                  <>Or buy now for ${price} · skip trial</>
                )}
              </Button>
            </>
          )}
        </div>

        <p className="text-[11px] text-white/35 text-center mt-4 leading-relaxed">
          Pro sits on top of your existing plan. Premium features stay
          unchanged. Cancel anytime during the trial — we don't store a
          card. Works on Chrome, Edge, and Brave (desktop + Android).
        </p>
      </div>
    </div>
  );
};

export default ProUpgradeModal;
